import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {ChartCard, ReportHorizontalBarChart} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {findReportBySlug} from './reportCatalogue';

/**
 * PRT2 — Adhoc Print Usage.
 *
 * Outlier detection, so the chart is ranked by RATE with the network average
 * drawn as a benchmark line — an outlier only reads as one against a baseline.
 *
 * Two traps the design avoids. Ranking by adhoc count would put the biggest
 * printer at the top for printing a lot of everything. Ranking by rate alone
 * would put a franchise with 62 prints, all adhoc, at 100%. So low-volume
 * franchises are shown muted and excluded from the headline.
 */

const SLUG = 'adhoc-prints';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const number = (v: any) => new Intl.NumberFormat('en-GB').format(Number(v) || 0);

const RELIABILITY_COLOURS: Record<string, string> = {
  reliable: '#B04A5A',
  thin: '#C9C4C0'
};

const AdhocPrints = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Adhoc Prints');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const byFranchise = collections?.series?.by_franchise || [];
  const bySubject = collections?.series?.by_subject || [];
  const detail = collections?.detail;

  // The API sorts by adhoc count for the table; the chart wants rate order.
  const chartData = useMemo(
    () =>
      [...byFranchise]
        .sort((a: any, b: any) => b.adhoc_rate - a.adhoc_rate)
        .map((f: any) => ({...f, band: f.reliable ? 'reliable' : 'thin'})),
    [byFranchise]
  );

  const networkRate = collections?.kpis?.find((k: any) => k.key === 'network_rate')?.value;

  const columns = [
    {name: 'Franchise', selector: (row: any) => row.franchise || '---', wrap: true},
    {name: 'Total prints', width: '130px', right: true, selector: (row: any) => number(row.total_prints)},
    {name: 'Adhoc', width: '100px', right: true, selector: (row: any) => number(row.adhoc)},
    {
      name: 'Adhoc rate',
      width: '120px',
      right: true,
      cell: (row: any) => (
        <span className={row.reliability === 'Reliable' ? 'font-weight-bold' : 'text-muted'}>
          {row.adhoc_rate}%
        </span>
      )
    },
    {name: 'Students', width: '110px', right: true, selector: (row: any) => number(row.students)},
    {
      name: 'Reliability',
      width: '180px',
      cell: (row: any) => (
        <span className={`badge badge-${row.reliability === 'Reliable' ? 'success' : 'secondary'}`}>
          {row.reliability}
        </span>
      )
    }
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Adhoc Print Usage'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Adhoc Prints</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, subject: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-lg-8">
              <ChartCard
                title="Adhoc rate by franchise"
                subtitle="Against the network average. Faded bars are franchises with too few prints for the rate to mean much."
                loading={loading}
                isEmpty={!chartData.length}
                height={420}>
                <ReportHorizontalBarChart
                  data={chartData}
                  labelKey="franchise"
                  valueKey="adhoc_rate"
                  currency={false}
                  height={380}
                  labelWidth={200}
                  colourField="band"
                  colourMap={RELIABILITY_COLOURS}
                  referenceValue={networkRate}
                  referenceLabel={`network ${networkRate}%`}
                />
              </ChartCard>
            </div>
            <div className="col-lg-4">
              <ChartCard
                title="Adhoc by subject"
                subtitle="Where off-plan printing concentrates"
                loading={loading}
                isEmpty={!bySubject.length}
                height={420}>
                <ReportHorizontalBarChart
                  data={bySubject}
                  labelKey="subject"
                  valueKey="adhoc"
                  currency={false}
                  height={380}
                  labelWidth={110}
                  colour="#C9873A"
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">Adhoc usage by franchise</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                noDataComponent={
                  <div className="text-muted py-4">No adhoc printing in this period.</div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdhocPrints;
